import React, { type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Link as MuiLink,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, logout, user, is_admin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const [managementAnchorEl, setManagementAnchorEl] = React.useState<null | HTMLElement>(null);
  const managementMenuOpen = Boolean(managementAnchorEl);

  const handleManagementMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setManagementAnchorEl(event.currentTarget);
  };

  const handleManagementMenuClose = () => {
    setManagementAnchorEl(null);
  };

  const [standingsAnchorEl, setStandingsAnchorEl] = React.useState<null | HTMLElement>(null);
  const standingsMenuOpen = Boolean(standingsAnchorEl);

  const handleStandingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setStandingsAnchorEl(event.currentTarget);
  };

  const handleStandingsMenuClose = () => {
    setStandingsAnchorEl(null);
  };

  const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    setProfileAnchorEl(null);
    logout();
    navigate('/login');
  };

  const userInitial = (user?.username || 'U').charAt(0).toUpperCase();

  const standingsItems = [
    { label: 'Standings', to: '/standings' },
    { label: 'Reports', to: '/reports' },
  ];

  const managementGroups = is_admin
    ? [
        {
          label: 'Setup',
          items: [
            { label: 'Tracks', to: '/tracks' },
            { label: 'Divisions', to: '/divisions' },
            { label: 'Regions', to: '/regions' },
            { label: 'Season Setup', to: '/season-setup' },
          ],
        },
        {
          label: 'Data',
          items: [
            { label: 'Points Schemas', to: '/points-schemas' },
            { label: 'Track Assignments', to: '/track-assignments' },
            { label: 'Division Mappings', to: '/division-mappings' },
            { label: 'Driver Location Scraper', to: '/drivers/location-scraper' },
          ],
        },
        {
          label: 'Admin',
          items: [
            { label: 'Users', to: '/admin/user-management' },
            { label: 'Report Recipients', to: '/admin/report-recipients' },
            { label: 'Logs', to: '/admin/logs' },
          ],
        },
      ]
    : [];

  const hasManagement = managementGroups.some((g) => g.items.length > 0);

  const primaryItems = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Race Entry', to: '/race-entry' },
    { label: 'Standings', to: '/standings' },
    { label: 'Reports', to: '/reports' },
    { label: 'Drivers', to: '/drivers' },
  ];

  const mobileDrawer = (
    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      <Box sx={{ width: 260 }} role="presentation">
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Welcome, {user?.username || 'User'}!
          </Typography>
        </Box>
        <Divider />
        <List>
          {primaryItems.map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton component={RouterLink} to={item.to} onClick={() => setDrawerOpen(false)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {hasManagement && (
          <>
            <Divider />
            {managementGroups.map((group) => (
              <List key={group.label} dense>
                <ListItem>
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10, letterSpacing: '0.04em' }}>
                    {group.label}
                  </Typography>
                </ListItem>
                {group.items.map((item) => (
                  <ListItem key={item.to} disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={item.to}
                      onClick={() => setDrawerOpen(false)}
                    >
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ))}
          </>
        )}
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); handleLogout(); }}>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky">
        <Toolbar>
          <MuiLink
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              color: 'inherit',
              textDecoration: 'none'
            }}
          >
            <Box
              component="img"
              src="/images/DIRTcarLogo.png"
              alt="DIRTcar"
              sx={{ height: 36, width: 'auto', display: 'block' }}
            />
            <Box
              sx={{
                borderLeft: '1px solid rgba(255,255,255,0.3)',
                pl: 1.5,
                py: 0.25
              }}
            >
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ color: '#ffffff', fontWeight: 600, lineHeight: 1.1 }}
              >
                Points Management
              </Typography>
            </Box>
          </MuiLink>
          {isAuthenticated ? (
            <>
              {isMobile ? (
                <IconButton color="inherit" onClick={() => setDrawerOpen(true)} edge="end">
                  <MenuIcon />
                </IconButton>
              ) : (
                <>
                  <Button color="inherit" component={RouterLink} to="/dashboard">
                    Dashboard
                  </Button>
                  <Button color="inherit" component={RouterLink} to="/race-entry">
                    Race Entry
                  </Button>
                  <Button
                    color="inherit"
                    onClick={handleStandingsMenuOpen}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                  >
                    Standings
                  </Button>
                  <Menu
                    anchorEl={standingsAnchorEl}
                    open={standingsMenuOpen}
                    onClose={handleStandingsMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{ paper: { sx: { mt: 0.5, minWidth: 160 } } }}
                  >
                    {standingsItems.map((item) => (
                      <MenuItem
                        key={item.to}
                        component={RouterLink}
                        to={item.to}
                        onClick={handleStandingsMenuClose}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                  <Button color="inherit" component={RouterLink} to="/drivers">
                    Drivers
                  </Button>
                  {hasManagement && (
                    <>
                      <Button
                        color="inherit"
                        onClick={handleManagementMenuOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                      >
                        Manage
                      </Button>
                      <Menu
                        anchorEl={managementAnchorEl}
                        open={managementMenuOpen}
                        onClose={handleManagementMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
                        MenuListProps={{
                          'aria-labelledby': 'management-button',
                          sx: { py: 0.5 },
                        }}
                      >
                        {managementGroups.map((group, groupIdx) => [
                          groupIdx > 0 && <Divider key={`${group.label}-divider`} sx={{ my: 0.5 }} />,
                          <Typography
                            key={`${group.label}-label`}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              px: 2,
                              pt: 0.75,
                              pb: 0.25,
                              display: 'block',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              fontSize: 10,
                              fontWeight: 500,
                            }}
                          >
                            {group.label}
                          </Typography>,
                          ...group.items.map((item) => (
                            <MenuItem
                              key={item.to}
                              component={RouterLink}
                              to={item.to}
                              onClick={handleManagementMenuClose}
                              sx={{ fontSize: 13 }}
                            >
                              {item.label}
                            </MenuItem>
                          )),
                        ])}
                      </Menu>
                    </>
                  )}
                  <Tooltip title={user?.username || 'Account'}>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      sx={{ ml: 1, p: 0.5 }}
                      aria-label="account menu"
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: '#ffffff',
                          fontSize: 13,
                          fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.3)'
                        }}
                      >
                        {userInitial}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={profileAnchorEl}
                    open={profileMenuOpen}
                    onClose={handleProfileMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { mt: 0.5, minWidth: 200 } } }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Signed in as
                      </Typography>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {user?.username || 'User'}
                      </Typography>
                      {is_admin && (
                        <Typography variant="caption" color="primary.main">
                          Administrator
                        </Typography>
                      )}
                    </Box>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ gap: 1 }}>
                      <LogoutIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )}
              {mobileDrawer}
            </>
          ) : (
            <>
              <Button color="inherit" component={RouterLink} to="/login">
                Login
              </Button>
              <Button color="inherit" component={RouterLink} to="/register">
                Register
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {children}
      </Container>
      <Box
        component="footer"
        sx={{
          py: 1.5,
          px: 3,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            &copy; {new Date().getFullYear()} World Racing Group
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            v{__APP_VERSION__}
            {' · '}
            {__APP_COMMIT__ === 'dev' ? (
              'dev'
            ) : (
              <MuiLink
                href={`https://github.com/ChrisOwens62/dirtcar-points-2025/commit/${__APP_COMMIT__}`}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="hover"
                title={__APP_COMMIT__}
              >
                {__APP_COMMIT__.slice(0, 7)}
              </MuiLink>
            )}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}; 